import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { flatMap, get, isEmpty, partition } from 'lodash'

import { Spinner } from '@/components/ui'
import { useGetThreadsList } from '@/features/annotation-management'
import { useFetchUntilComment } from '@/hooks/useFetchUntilComment'
import EmptyCommentImage from '@/public/images/empty-comment.svg'
import { ResponseThread } from '@/schema'

import { useCommentStore } from '../../store'
import { ECommentStatus } from '../../types'

import { CommentGroup } from './CommentGroup'

type CommentListProps = {
  documentVersionId: string | undefined
  targetCommentId?: string | null
  onTargetCommentFound?: (comment: ResponseThread) => void
  onTargetCommentNotFound?: () => void
}

export const CommentList: FC<CommentListProps> = ({
  documentVersionId,
  targetCommentId,
  onTargetCommentFound,
  onTargetCommentNotFound,
}) => {
  const { t } = useTranslation()

  const loadMoreRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // Use the thread list query directly
  const queryResult = useGetThreadsList({
    documentVersionId: documentVersionId ?? '',
  })

  const { data, hasNextPage, isFetchingNextPage, fetchNextPage } = queryResult

  // Handle progressive fetching for target comment
  const { isFetchingUntilFound } = useFetchUntilComment(
    targetCommentId || null,
    queryResult,
    onTargetCommentFound,
    onTargetCommentNotFound,
    !!targetCommentId && !!documentVersionId
  )

  const { setComments } = useCommentStore()

  // Store comments in Zustand as they're fetched
  const comments = useMemo(() => {
    return flatMap(data?.pages, (page) => page?.data) || []
  }, [data])

  useEffect(() => {
    if (comments && comments.length > 0 && documentVersionId) {
      setComments(documentVersionId, comments)
    }
  }, [comments, documentVersionId, setComments])

  // Handle intersection observer for infinite scroll
  const stateRef = useRef({
    hasNextPage,
    isFetchingNextPage,
    isFetchingUntilFound,
  })

  // Update ref without triggering re-renders
  useEffect(() => {
    stateRef.current = {
      hasNextPage,
      isFetchingNextPage,
      isFetchingUntilFound,
    }
  }, [hasNextPage, isFetchingNextPage, isFetchingUntilFound])

  // Stable callback that uses ref for latest values
  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const entry = entries[0]
      const { hasNextPage, isFetchingNextPage, isFetchingUntilFound } =
        stateRef.current

      if (
        entry?.isIntersecting &&
        hasNextPage &&
        !isFetchingNextPage &&
        !isFetchingUntilFound
      ) {
        fetchNextPage()
      }
    },
    [fetchNextPage]
  )

  // Set up IntersectionObserver only once
  useEffect(() => {
    const targetElement = loadMoreRef.current
    const rootElement = scrollContainerRef.current

    if (!targetElement) return

    const observer = new IntersectionObserver(handleIntersection, {
      root: rootElement || null,
      rootMargin: '100px',
      threshold: 0.1,
    })

    observer.observe(targetElement)

    return () => {
      observer.disconnect()
    }
  }, [handleIntersection])

  const { commentStatus, commentsByDocVersion } = useCommentStore()

  // Layout version counter: increments when any CommentGroup's open state changes
  // This triggers all CommentGroups to recalculate their sticky positions
  const [layoutVersion, setLayoutVersion] = useState(0)
  const handleLayoutChange = useCallback(() => {
    setLayoutVersion((prev) => prev + 1)
  }, [])

  // Get comments from store (now populated by effect above)
  const storedComments = useMemo(() => {
    if (!documentVersionId) return []
    const commentsById = get(commentsByDocVersion, documentVersionId, {})
    return Object.values(commentsById)
  }, [commentsByDocVersion, documentVersionId])

  // Filter and group comments using lodash for cleaner code
  const { fileComments, sectionComments } = useMemo(() => {
    // Early return if no stored comments
    if (isEmpty(storedComments)) {
      return { fileComments: [], sectionComments: [] }
    }

    // Filter by status first
    const filteredComments =
      commentStatus === ECommentStatus.ALL
        ? storedComments
        : storedComments.filter(
            (comment) => comment.thread_status === commentStatus
          )

    // Partition into section (with annotation) and file (without annotation)
    const [sectionComments, fileComments] = partition(
      filteredComments,
      (comment) => !!comment.annotation
    )

    return { fileComments, sectionComments }
  }, [storedComments, commentStatus])

  const hasComments = !isEmpty(fileComments) || !isEmpty(sectionComments)

  if (!hasComments) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <img src={EmptyCommentImage} alt="No search results" className="mb-4" />
        <div className="font-medium text-md text-primary-900">
          {t('tflViewer.commentSidebar.emptyState.title')}
        </div>
      </div>
    )
  }

  return (
    <div
      id="comment-list-container"
      ref={scrollContainerRef}
      className="relative flex flex-col flex-1 min-h-0 overflow-y-auto"
    >
      <CommentGroup
        title="File"
        comments={fileComments}
        defaultOpen
        isFirst
        layoutVersion={layoutVersion}
        onLayoutChange={handleLayoutChange}
      />
      <CommentGroup
        title="Section"
        comments={sectionComments}
        defaultOpen
        layoutVersion={layoutVersion}
        onLayoutChange={handleLayoutChange}
      />
      <div ref={loadMoreRef} className="w-full h-20" />
      {(isFetchingNextPage || isFetchingUntilFound) && (
        <div className="relative flex items-center justify-center p-6">
          <Spinner className="mx-auto size-8 text-brand-600" />
        </div>
      )}
    </div>
  )
}
